import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { CompetitorData } from '~/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import { useTabsNavigation } from '~/widgets/theme-widget/tabs/hooks/useTabsNavigation';
import { SubTabsType } from '~/widgets/theme-widget/ThemeWidget';
import * as S from '~/widgets/theme-widget/ThemeWidget.styles';

import { CompetitorDetail } from '../tabs/competitor-detail/CompetitorDetail';
import { CompetitorOverview } from '../tabs/competitor-overview/CompetitorOverview';

type CompetitorsWidgetProps = {
  entity: Entity;
  competitorData: CompetitorData;
};

export const CompetitorsWidget = ({
  entity,
  competitorData,
}: CompetitorsWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();

  const subTabs = [
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.title'
      ),
      key: SubTabsType.CompetitorOverview,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.competitors.tabs.competitorDetail.title'
      ),
      key: SubTabsType.CompetitorDetail,
    },
  ];

  const { activeIndex, onTabChange } = useTabsNavigation(
    subTabs,
    SubTabsType.CompetitorOverview
  );

  const renderContent = () => {
    if (!competitorData) {
      throw new Error('Failed to load CompetitorsWidget');
    }

    if (
      params[queryKeys.activeDrawerTab] === SubTabsType.CompetitorDetail &&
      !!competitorData.entity
    ) {
      return (
        <CompetitorDetail entity={entity} competitor={competitorData.entity} />
      );
    }

    return <CompetitorOverview entity={entity} competitor={competitorData} />;
  };

  return (
    <S.Container>
      <S.StyledButtonGroup
        labels={subTabs.map(t => t.label)}
        activeIndex={activeIndex}
        onClick={onTabChange}
      />
      <ErrorBoundaryWrapper>{renderContent()}</ErrorBoundaryWrapper>
    </S.Container>
  );
};
